'use client';

import { medusaClient } from '@lib/config';
import { Customer } from '@medusajs/medusa';
import { useMutation } from '@tanstack/react-query';
import { useMeCustomer } from 'medusa-react';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export enum LOGIN_VIEW {
    SIGN_IN = 'sign-in',
    MFA = 'mfa',
    REGISTER = 'register',
    FORGOT_PASSWORD = 'forgot-password',
}

interface mfaInfo {
    email: string,
    password: string,
    customer_id: string,
    display_name: string
}

interface AccountContext {
    customer?: Omit<Customer, 'password_hash'>;
    retrievingCustomer: boolean;
    loginView: [LOGIN_VIEW, React.Dispatch<React.SetStateAction<LOGIN_VIEW>>];
    checkSession: () => void;
    refetchCustomer: () => void;
    handleLogout: () => void;
    mfaInfo?: mfaInfo | null,
    setMFAInfo: (req: mfaInfo | null) => void,
    isSendMFACode: boolean | null,
    setIsSendMFACode: (req: boolean | null) => void,
    getMFARememberKey: () => string,
}

const AccountContext = createContext<AccountContext | null>(null);

interface AccountProviderProps {
    children?: React.ReactNode;
}

const handleDeleteSession = () => {
    return medusaClient.auth.deleteSession();
};

export const AccountProvider = ({ children }: AccountProviderProps) => {
    const {
        customer,
        isLoading: retrievingCustomer,
        refetch,
        remove,
    } = useMeCustomer({
        onError: () => {
        },
    });
    const searchParams = useSearchParams();
    const view = (searchParams.get('view') as LOGIN_VIEW) ?? LOGIN_VIEW.SIGN_IN;
    const loginView = useState<LOGIN_VIEW>(view);
    const [mfaInfo, setMFAInfo] = useState<mfaInfo | null>(null);
    const [isSendMFACode, setIsSendMFACode] = useState<boolean | null>(false);

    const router = useRouter();

    const checkSession = useCallback(() => {
        if (!customer && !retrievingCustomer) {
            router.push('/account/login');
        }
    }, [customer, retrievingCustomer, router]);

    const useDeleteSession = useMutation({
        mutationFn: handleDeleteSession,
        mutationKey: ['delete-session'],
    });

    const handleLogout = () => {
        useDeleteSession.mutate(undefined, {
            onSuccess: () => {
                remove();
                loginView[1](LOGIN_VIEW.SIGN_IN);
                router.push('/');
            },
        });
    };

    const getMFARememberKey = (): string => {
        return 'remember_mfa';
    };

    const contextValue = useMemo(
        () => ({
            customer,
            retrievingCustomer,
            loginView,
            checkSession,
            refetchCustomer: refetch,
            handleLogout,
            mfaInfo,
            setMFAInfo,
            isSendMFACode,
            setIsSendMFACode,
            getMFARememberKey,
        }),
        [
            customer,
            retrievingCustomer,
            loginView,
            checkSession,
            refetch,
            handleLogout,
            mfaInfo,
            isSendMFACode,
        ]
    );
    return (
        <AccountContext.Provider
            value={contextValue}
        >
            {children}
        </AccountContext.Provider>
    );
};

export const useAccount = () => {
    const context = useContext(AccountContext);

    if (context === null) {
        throw new Error('useAccount must be used within a AccountProvider');
    }
    return context;
};
